import { ModalFooter } from '@components/modals/modal-footer';
import { ModalHeader } from '@components/modals/modal-header';
import { ModalV2 } from '@components/modals/modal-v2/src/modal-v2';
import classNames from 'classnames';

import { ModalWithLayoutProps } from './modal-with-layout.types';

/**
 * ModalWithLayout - Reusable modal component with consistent header/footer layout
 *
 * This component provides a standardized structure for modals with:
 * - ModalHeader (new Figma design with integrated subtitle)
 * - Scrolleable content area
 * - ModalStickyFooter (optional, with primary/secondary/tertiary buttons)
 * - Optional form support
 *
 * @example
 * // Basic modal with header and footer
 * <ModalWithLayout
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Modal Title"
 *   footer={{
 *     primaryButtonText: "Save",
 *     secondaryButtonText: "Cancel",
 *     onPrimaryClick: handleSave,
 *     onSecondaryClick: onClose,
 *   }}
 * >
 *   <p>Modal content</p>
 * </ModalWithLayout>
 *
 * @example
 * // Form modal with submit
 * <ModalWithLayout
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Form Title"
 *   isForm
 *   onSubmit={handleSubmit}
 *   footer={{
 *     primaryButtonText: "Save",
 *     isSubmit: true,
 *     isPrimaryDisabled: !isValid,
 *   }}
 * >
 *   <input name="field" />
 * </ModalWithLayout>
 *
 * @example
 * // Modal with subtitle and edit button
 * <ModalWithLayout
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Add tickets to group"
 *   subtitle="You're adding 4 tickets to Day 1 - August 7th."
 *   onClickEditTitle={() => console.log('edit')}
 * >
 *   <DragAndDropList />
 * </ModalWithLayout>
 */
export const ModalWithLayout = ({
  // Modal base (required)
  isOpen,
  onClose,
  children,

  // Modal base (optional)
  variant = 'md',
  closeOnBackdropClick = true,
  closeOnEscape = true,

  // Content
  contentClassName,

  // Footer
  footer,

  // Form support
  isForm = false,
  onSubmit,

  showCloseButton = true,

  ...headerProps
}: ModalWithLayoutProps) => {
  const content = (
    <>
      <ModalHeader {...headerProps} handleClose={showCloseButton ? onClose : undefined} />

      <div className={classNames('py-6 px-4 sm:px-12', contentClassName)}>{children}</div>

      {footer && <ModalFooter {...footer} />}
    </>
  );

  const wrappedContent = isForm && onSubmit ? <form onSubmit={onSubmit}>{content}</form> : content;

  return (
    <ModalV2
      isOpen={isOpen}
      onClose={onClose}
      variant={variant}
      closeOnBackdropClick={closeOnBackdropClick}
      closeOnEscape={closeOnEscape}>
      {wrappedContent}
    </ModalV2>
  );
};
